import {
  type AccountSelectorContextProviderEntityQuery,
  type OrganizationSubscriptionBasicInfoByNameQuery,
  OrganizationSubscriptionType,
} from '../../../generated/graphql';
import {getAccountType} from '../../../pages/Billing/AccountSettings/util';
import {PlanName} from '../../../pages/Billing/util';
import {EntityType} from '../../../pages/EntityPage/useEntityPageData';
import {type Viewer} from '../../../state/viewer/types';
import {type UserInfo} from '../../../types/graphql';
import {
  getAccountPlanTypeFromPrimarySub,
  getPrimarySub,
} from '../../../util/accounts/pricing';
import {
  type AccountSelectorStorageKeys,
  safelyParseAccountJson,
} from './AccountSelectorContextProvider';
import {type Account, AccountPlanType, AccountType} from './types';

export type AccountSelectorViewer = Pick<
  Viewer,
  'id' | 'username' | 'teams' | 'defaultEntity'
>;

export function isLegacyPersonalEntity(
  entity: AccountSelectorContextProviderEntityQuery['entity']
): boolean {
  // Some supported deployments still have personal rows created before entityType was backfilled.
  return (
    entity?.entityType === EntityType.Personal ||
    (entity?.entityType == null && entity?.isTeam === false)
  );
}

function getDefaultAccount(
  viewer: AccountSelectorViewer,
  accountList: Account[]
): Account | null {
  const defaultEntityOrganization = viewer.teams?.edges?.find(
    team => team.node.id === viewer.defaultEntity?.id
  );
  const account = accountList.find(
    o => o.id === defaultEntityOrganization?.node.organizationId
  );
  const isPersonalEntity =
    defaultEntityOrganization?.node.name === viewer?.username;

  let personalPaidAccount = null;
  if (account == null) {
    personalPaidAccount = accountList.find(
      accountItem =>
        accountItem?.accountType === AccountType.Personal &&
        accountItem.personalOrgId != null
    );
  }
  if (account) {
    return {
      name: account?.name ?? '',
      id: account?.id ?? '',
      accountType: isPersonalEntity
        ? AccountType.Personal
        : account?.accountType,
      accountPlanType: isPersonalEntity
        ? AccountPlanType.Personal
        : account?.accountPlanType,
      isEnterprise: account?.isEnterprise,
      memberCount: account?.memberCount,
      username: account?.username,
    };
  }

  if (personalPaidAccount) {
    return personalPaidAccount;
  }

  // Fall back to the first available account, or null when the user
  // has no accounts at all (e.g. no personal entity and no orgs).
  return accountList.length > 0 ? accountList[0] : null;
}

export function getAccountFromStorage(
  storage: {getItem: (key: string) => string | null},
  key: AccountSelectorStorageKeys,
  accountList: Account[]
): Account | null {
  const storageItem = storage.getItem(key);
  if (storageItem == null) {
    return null;
  }
  const account = safelyParseAccountJson(storageItem, accountList);
  if (account == null) {
    return null;
  }
  // Return the up-to-date account from the list rather than the
  // potentially stale storage value.
  return accountList.find(a => a.id === account.id) ?? null;
}

export type GetSelectedAccountParams = {
  selectedAccount: Account | null;
  lastSelectedAccount: Account | null;
  viewer: AccountSelectorViewer;
  accountList: Account[];
  userInfo: Pick<UserInfo, 'isPersonalEntityHidden'>;
  orgName: string | null | undefined;
  entityName: string | null | undefined;
  entityQuery: AccountSelectorContextProviderEntityQuery | undefined;
  personalEntityUserID?: string | null;
  orgQuery: OrganizationSubscriptionBasicInfoByNameQuery | undefined;
  isAdminMode: boolean;
  isProfilePath: boolean;
};

export function buildDummyOrganizationAccount(
  organization: NonNullable<
    OrganizationSubscriptionBasicInfoByNameQuery['organization']
  >,
  viewer: Pick<AccountSelectorViewer, 'username'> | null | undefined
): Account {
  const primarySub = getPrimarySub(organization);
  return {
    name: organization.name,
    accountType: getAccountType(organization),
    accountPlanType: getAccountPlanTypeFromPrimarySub(primarySub),
    id: organization.id,
    username: viewer?.username ?? '',
    memberCount: organization.memberCount,
    isEnterprise:
      primarySub?.plan?.name === PlanName.Enterprise &&
      primarySub?.subscriptionType !== OrganizationSubscriptionType.Academic &&
      primarySub?.subscriptionType !==
        OrganizationSubscriptionType.AcademicTrial,
    isDummyAccount: true,
  };
}

export function buildDummyPersonalAccount(
  entityName: string,
  ownerID: string,
  viewer: Pick<AccountSelectorViewer, 'username'> | null | undefined
): Account {
  return {
    name: entityName,
    accountType: AccountType.Personal,
    accountPlanType: AccountPlanType.Personal,
    id: ownerID,
    username: viewer?.username ?? '',
    isDummyAccount: true,
  };
}

// Returns only the account the URL names, or null when it names none. The
// "no org/entity in URL" reset to the default account lives in
// getSelectedAccount, which owns the full precedence chain.
export function getUrlAccount({
  viewer,
  accountList,
  userInfo,
  orgName,
  entityName,
  entityQuery,
  personalEntityUserID,
  orgQuery,
  isAdminMode,
  selectedAccount,
  isProfilePath,
}: GetSelectedAccountParams): Account | null {
  const orgID = orgQuery?.organization?.id;
  const orgAccount = orgID ? accountList.find(o => o.id === orgID) : null;
  if (orgAccount != null) {
    return orgAccount;
  }

  const isViewersPersonalEntity =
    orgAccount == null &&
    viewer?.username != null &&
    (entityName === viewer?.username || orgName === viewer?.username);

  const shouldHidePersonalEntity = userInfo?.isPersonalEntityHidden ?? false;

  // This is the personal account, so switch it
  if (
    isViewersPersonalEntity &&
    (!isProfilePath || selectedAccount == null) // If we're on the profile page, we don't want to switch to the personal account unless it hasn't been initialized yet
  ) {
    // If this is the personal account (and not the profile page)
    const personalAccount = accountList.find(
      account => account?.accountType === AccountType.Personal
    );
    if (personalAccount && !shouldHidePersonalEntity) {
      return personalAccount;
    }
  }
  const orgNameFromCurrentUrl =
    orgName ?? entityQuery?.entity?.organization?.name;
  const orgExists =
    orgNameFromCurrentUrl != null && orgQuery?.organization != null;
  // If there's an org in the URL, and it's not the personal account org
  if (
    orgAccount == null &&
    !isViewersPersonalEntity &&
    orgExists &&
    orgQuery?.organization != null
  ) {
    // We are viewing an org that we're not a member of, so create a dummy account for the account selector
    return {
      ...buildDummyOrganizationAccount(orgQuery.organization, viewer),
      name: orgNameFromCurrentUrl ?? '',
    };
  }

  // Build an admin-only dummy account for a foreign legacy personal entity.
  if (
    orgAccount == null &&
    !isViewersPersonalEntity &&
    !orgExists &&
    isLegacyPersonalEntity(entityQuery?.entity) &&
    isAdminMode &&
    personalEntityUserID != null &&
    personalEntityUserID !== ''
  ) {
    return buildDummyPersonalAccount(
      entityName ?? '',
      personalEntityUserID,
      viewer
    );
  }

  return null;
}

function validateSelectedAccount(
  accountList: Account[],
  selectedAccount: Account | null,
  isAdminMode: boolean
) {
  // If user has a selected account that does not belong to the user
  // and user is not impersonating, then invalidate the selected account and last selected account by setting it to null.
  // This could happen if the user is kicked from their org but the account is still selected in session storage for example
  const isAccountListMissingSelectedAccount =
    accountList.every(a => a.name !== selectedAccount?.name) &&
    accountList.length > 0;

  // Dummy accounts don't appear in the account list, but shouldn't
  // be invalidated if they're currently in admin mode.
  const isValidDummyAccount =
    selectedAccount?.isDummyAccount === true && isAdminMode;

  if (
    selectedAccount &&
    isAccountListMissingSelectedAccount &&
    !isValidDummyAccount
  ) {
    return null;
  }
  return selectedAccount;
}

export function getSelectedAccount(
  params: GetSelectedAccountParams
): Account | null {
  const {
    viewer,
    accountList,
    orgName,
    entityName,
    entityQuery,
    isAdminMode,
    selectedAccount: selectedAccountFromSessionStorage,
    lastSelectedAccount: lastSelectedAccountFromLocalStorage,
  } = params;
  const defaultAccount = getDefaultAccount(viewer, accountList);

  const selectedAccount = validateSelectedAccount(
    accountList,
    selectedAccountFromSessionStorage,
    isAdminMode
  );
  const lastSelectedAccount = validateSelectedAccount(
    accountList,
    lastSelectedAccountFromLocalStorage,
    isAdminMode
  );

  // The organization associated with the orgName in the URL gets precedence over the
  // entity's org (in the event that they don't match).
  const orgNameFromCurrentUrl =
    orgName ?? entityQuery?.entity?.organization?.name;
  const urlResolutionApplies =
    selectedAccount?.name !== orgNameFromCurrentUrl || selectedAccount == null;
  const selectedAccountFromURL = urlResolutionApplies
    ? getUrlAccount(params)
    : null;
  if (selectedAccountFromURL != null) {
    return selectedAccountFromURL;
  }

  // If there's no entity or org in the URL, but selected account was previously selected to be a dummy account,
  // we should switch back to the default account (the last selected account may also be a dummy account somehow).
  // This could happen if in admin mode viewing an org's page, then clicking the home button for example.
  const orgOrEntityInUrl = orgName != null || entityName != null;
  if (
    urlResolutionApplies &&
    !orgOrEntityInUrl &&
    selectedAccount?.isDummyAccount === true
  ) {
    return defaultAccount;
  }
  return selectedAccount ?? lastSelectedAccount ?? defaultAccount;
}

export function getCanSeeOrgInURL({
  orgQuery,
  entityQuery,
  orgName,
  entityName,
  viewer,
}: GetSelectedAccountParams) {
  // If there's an org in the URL, but no organization is returned, this means it's an org
  // that doesn't exist or we don't have permissions to see
  const orgOrEntityInUrl = orgName != null || entityName != null;
  const cannotSeeOrgFromUrl =
    orgOrEntityInUrl &&
    orgQuery?.organization == null &&
    entityQuery?.entity?.organization == null;
  const isViewersPersonalEntity =
    viewer.username != null &&
    (entityName === viewer.username || orgName === viewer.username);
  return isViewersPersonalEntity || !cannotSeeOrgFromUrl;
}
